/**
 * Team & Access → Permissions (`access.permissions.view`): every action on
 * every entity, and whether each role holds it. Read-only while the roles are
 * fixed (Admin holds everything); custom groups will make it editable, their
 * columns beside the roles'.
 *
 * A toolbar (search by action, entity or key; a Module filter with counts),
 * then one table grouped by module and entity: each group is a `<tbody>`
 * opened by a `th[scope=rowgroup]` row, so a screen reader announces where it
 * starts. A grant reads as words (Granted / Not granted), never only as a
 * colour or a glyph. The catalog is small and bounded, so it is not paged and
 * every filter runs in the browser.
 */

import { useMemo, useState } from 'react';
import { Check, Search, ShieldCheck } from 'lucide-react';

import { usePermissionMatrix } from '@/api/queries';
import type { PermissionAction, PermissionMatrix } from '@/api/types';
import { MultiSelect } from '@/ui/MultiSelect';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

/** How many actions the catalog holds: the tab's count. */
export function actionCount(matrix: PermissionMatrix): number {
  return matrix.modules.reduce((n, m) => n + m.entities.reduce((k, e) => k + e.actions.length, 0), 0);
}

function matches(action: PermissionAction, entity: string, needle: string): boolean {
  return [action.label, action.key, action.description, entity].some((text) => text.toLowerCase().includes(needle));
}

function GrantCell({ role, granted, scope }: { role: string; granted: boolean; scope: PermissionAction['scope'] }) {
  return (
    <td aria-label={`${role}: ${granted ? 'granted' : 'not granted'}${granted && scope === 'own' ? ', own only' : ''}`}>
      {granted ? (
        <span className="row grant-cell">
          <Check aria-hidden="true" className="grant-icon" />
          <span className="hint">Granted</span>
          {scope === 'own' && <span className="badge badge-info">Own only</span>}
        </span>
      ) : (
        <span className="hint">Not granted</span>
      )}
    </td>
  );
}

type Group = { module: string; entity: string; actions: PermissionAction[] };

function PermissionGroups({ groups, roles }: { groups: Group[]; roles: PermissionMatrix['roles'] }) {
  const columns = 2 + roles.length;
  if (groups.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns}>
            <div className="empty">
              <div className="e-icon"><ShieldCheck aria-hidden="true" /></div>
              <h3>No permissions match</h3>
              <p>Clear the search or the module filter to see the rest.</p>
            </div>
          </td>
        </tr>
      </tbody>
    );
  }
  return (
    <>
      {groups.map((g) => (
        <tbody key={`${g.module}/${g.entity}`}>
          <tr className="group-head">
            <th scope="rowgroup" colSpan={columns}>
              {g.module} · {g.entity}
            </th>
          </tr>
          {g.actions.map((a) => (
            <tr key={a.key}>
              <td>
                <b>{a.label}</b>
                <span className="sub mono">{a.key}</span>
              </td>
              <td className="hint">{a.description}</td>
              {roles.map((r) => (
                <GrantCell key={r.key} role={r.label} granted={a.grants[r.key] ?? false} scope={a.scope} />
              ))}
            </tr>
          ))}
        </tbody>
      ))}
    </>
  );
}

export function PermissionsTab() {
  const matrix = usePermissionMatrix();
  const [search, setSearch] = useState('');
  const [modules, setModules] = useState<string[]>([]);

  const groups = useMemo<Group[]>(() => {
    const needle = search.trim().toLowerCase();
    return (matrix.data?.modules ?? [])
      .filter((m) => modules.length === 0 || modules.includes(m.name))
      .flatMap((m) =>
        m.entities.map((e) => ({
          module: m.name,
          entity: e.name,
          actions: e.actions.filter((a) => !needle || matches(a, e.name, needle)),
        })),
      )
      .filter((g) => g.actions.length > 0);
  }, [matrix.data, search, modules]);

  if (matrix.isPending) return <LoadingCard label="Loading permissions" />;
  if (matrix.error) return <LoadFailure error={matrix.error} onRetry={() => void matrix.refetch()} />;

  const { roles } = matrix.data;
  const moduleCount = (name: string) =>
    matrix.data.modules.find((m) => m.name === name)?.entities.reduce((n, e) => n + e.actions.length, 0) ?? 0;

  return (
    <>
      <div className="toolbar filter-panel">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search by action, entity or key"
            aria-label="Search by action, entity or key"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <MultiSelect
          label="Filter by module"
          allLabel="All modules"
          noun="modules"
          options={matrix.data.modules.map((m) => ({ value: m.name, label: m.name, meta: moduleCount(m.name) }))}
          value={modules}
          onChange={setModules}
        />
      </div>
      <p className="hint permissions-note">
        Roles are fixed for now. A person can hold several and gets every permission any of them grants; Admin holds every permission. Custom groups will make this editable.
      </p>

      <div className="table-scroll">
        <table className="data zebra compact grouped permissions-table">
          <thead>
            <tr>
              <th scope="col">Action</th>
              <th scope="col">Description</th>
              {roles.map((r) => (
                <th key={r.key} scope="col">{r.label}</th>
              ))}
            </tr>
          </thead>
          <PermissionGroups groups={groups} roles={roles} />
        </table>
      </div>
    </>
  );
}
